/**
 * @file CreatorNewsPage.jsx
 * @description "News from the creator" — the place the application's creator
 *              (the super admin) shares information about the software:
 *              what's new, what's coming, how something should be used.
 *
 * Every signed-in user can read it. Only the super admin sees the composer
 * and the edit/pin/delete controls (writes are server-enforced too).
 * Posts live in app_news via /api/app-info/news.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Card, CardContent, Stack, Skeleton, Alert, Chip,
    IconButton, Tooltip, Button, TextField, FormControlLabel, Switch,
} from '@mui/material';
import {
    Edit as EditIcon, Delete as DeleteIcon,
    PushPin as PinIcon, Send as SendIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { isSuperAdmin } from '../../utils/permissions';
import appInfoService from '../../services/appInfoService';
import { formatToIST } from '../../utils/dataUtils';

const CreatorNewsPage = () => {
    const { user } = useAuth();
    const superAdmin = isSuperAdmin(user);

    const [posts, setPosts] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    // Composer doubles as the editor: {id?} present = editing that post.
    const [draft, setDraft] = useState({ id: null, title: '', body: '', pinned: false });
    const clearDraft = () => setDraft({ id: null, title: '', body: '', pinned: false });

    const load = useCallback(async () => {
        try {
            const res = await appInfoService.listNews();
            setPosts(res?.posts || []);
            setError('');
        } catch (e) {
            setError(e?.message || 'Failed to load news');
            setPosts([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const publish = async () => {
        if (!draft.title.trim() || !draft.body.trim()) return;
        setBusy(true);
        try {
            if (draft.id) await appInfoService.updateNews(draft.id, { title: draft.title, body: draft.body, pinned: draft.pinned });
            else await appInfoService.createNews({ title: draft.title, body: draft.body, pinned: draft.pinned });
            clearDraft();
            await load();
        } catch (e) { setError(e?.message || 'Publish failed'); }
        finally { setBusy(false); }
    };

    const togglePin = async (p) => {
        setBusy(true);
        try { await appInfoService.updateNews(p.id, { pinned: !p.pinned }); await load(); }
        catch (e) { setError(e?.message || 'Pin failed'); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        setBusy(true);
        try { await appInfoService.deleteNews(id); if (draft.id === id) clearDraft(); await load(); }
        catch (e) { setError(e?.message || 'Delete failed'); }
        finally { setBusy(false); }
    };

    return (
        <Box p={3} className="animate-fade-in">
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Stack spacing={2} sx={{ maxWidth: 860 }}>
                {superAdmin && (
                    <Card variant="outlined" sx={{ borderRadius: '14px', borderColor: 'primary.light' }}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                                {draft.id ? `Editing post #${draft.id}` : 'Share an update with every user'}
                            </Typography>
                            <Stack spacing={1.5}>
                                <TextField label="Title" size="small" fullWidth value={draft.title}
                                    onChange={e => setDraft(p => ({ ...p, title: e.target.value }))} />
                                <TextField label="What do you want everyone to know?" fullWidth multiline minRows={4}
                                    value={draft.body}
                                    onChange={e => setDraft(p => ({ ...p, body: e.target.value }))} />
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Button variant="contained" startIcon={<SendIcon />} onClick={publish}
                                        disabled={busy || !draft.title.trim() || !draft.body.trim()}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                                        {busy ? 'Publishing…' : draft.id ? 'Save changes' : 'Publish'}
                                    </Button>
                                    {draft.id && <Button onClick={clearDraft}>Cancel edit</Button>}
                                    <FormControlLabel sx={{ ml: 'auto' }}
                                        control={<Switch size="small" checked={draft.pinned}
                                            onChange={e => setDraft(p => ({ ...p, pinned: e.target.checked }))} />}
                                        label={<Typography variant="body2">Pin to top</Typography>} />
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {posts === null ? (
                    [1, 2].map(i => <Skeleton key={i} variant="rounded" height={130} />)
                ) : posts.length === 0 ? (
                    <Card variant="outlined" sx={{ borderRadius: '14px' }}>
                        <CardContent>
                            <Typography color="text.secondary">
                                Nothing has been posted yet.
                                {superAdmin ? ' Your first update will appear here for every user.' : ' Check back soon.'}
                            </Typography>
                        </CardContent>
                    </Card>
                ) : posts.map(p => (
                    <Card key={p.id} variant="outlined" sx={{ borderRadius: '14px' }}>
                        <CardContent sx={{ '&:last-child': { pb: 2.5 } }}>
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    {!!p.pinned && <PinIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                                    <Typography variant="subtitle1" fontWeight={800}>{p.title}</Typography>
                                </Stack>
                                {superAdmin && (
                                    <Stack direction="row" spacing={0.5}>
                                        <Tooltip title={p.pinned ? 'Unpin' : 'Pin to top'}>
                                            <IconButton size="small" disabled={busy} onClick={() => togglePin(p)}>
                                                <PinIcon fontSize="small" color={p.pinned ? 'warning' : 'inherit'} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton size="small"
                                                onClick={() => setDraft({ id: p.id, title: p.title, body: p.body, pinned: !!p.pinned })}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton size="small" color="error" disabled={busy} onClick={() => remove(p.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                )}
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7, mb: 1 }}>
                                {p.body}
                            </Typography>
                            <Chip size="small" variant="outlined"
                                label={`${p.created_by_name || 'Creator'} · ${formatToIST(p.created_at)}`}
                                sx={{ fontWeight: 600 }} />
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        </Box>
    );
};

export default CreatorNewsPage;
